import { useState, useEffect } from "react"
import { Box, Flex, Text, Badge } from "@chakra-ui/react"
import axios from "axios"
import BentoCard from "../BentoCard"
import DetailsModal from "../DetailsModal"
import ArticleStatus from "../../ArticleStatus"
import { neumorph } from "../neumorph"
import { formatCurrency, formatNum } from "../KpiHeader"
import { statusLabel, statusColor, isRangedStatus } from "../../statusCodes"

const ArticleStatusCard = ({ supplier, retailer }) => {
  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState([])
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    // Every retailer (2026-10-07): PnP codes, Checkers / MediRite / Petshop 1 / 0, Clicks A / N / I, Dis-Chem YES / NO.
    if (!supplier || !retailer) {
      setSummary([])
      return
    }
    const controller = new AbortController()
    const fetchData = async () => {
      setLoading(true)
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/insights/article-status`,
          { supplier, retailer, userid: localStorage.getItem("userid") },
          { signal: controller.signal }
        )
        if (res.data?.status === 1) setSummary(res.data.data?.summary || [])
        else setSummary([])
      } catch (err) {
        if (!axios.isCancel(err)) console.error("ArticleStatusCard fetch error:", err)
      } finally {
        // a cancelled request (retailer / supplier switched) must not end the NEW request\'s loading state
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    fetchData()
    return () => controller.abort()
  }, [supplier, retailer])

  const ranged = summary.filter((r) => isRangedStatus(r.status_code, retailer))
  const rangedValue = ranged.reduce((s, r) => s + (parseFloat(r.total_soh_value) || 0), 0)
  const nonActive = summary.filter((r) => !isRangedStatus(r.status_code, retailer))
  const totalNonActiveValue = nonActive.reduce((s, r) => s + (parseFloat(r.total_soh_value) || 0), 0)

  return (
    <>
      <BentoCard
        title="📊 Article Status"
        subtitle="Ranged vs not ranged — stock in stores"
        loading={loading}
        onDetails={() => setModalOpen(true)}
      >
        {summary.length === 0 ? (
          <Text fontSize="xs" color={neumorph.textMuted}>
            No data
          </Text>
        ) : (
          <Flex direction="column" h="100%" gap={2}>
            <Flex gap={3}>
              <Box>
                <Text fontSize="2xs" color={neumorph.textMuted}>
                  Ranged SOH
                </Text>
                <Text fontSize="md" fontWeight="700" color={neumorph.accentGreen}>
                  {formatCurrency(rangedValue)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="2xs" color={neumorph.textMuted}>
                  Not-ranged SOH
                </Text>
                <Text fontSize="md" fontWeight="700" color={neumorph.accentOrange}>
                  {formatCurrency(totalNonActiveValue)}
                </Text>
              </Box>
            </Flex>
            <Box mt={1}>
              <Text fontSize="2xs" color={neumorph.textMuted} mb={1} fontWeight="600" textTransform="uppercase">
                Status breakdown
              </Text>
              {summary.slice(0, 5).map((s) => (
                <Flex
                  key={s.status_code}
                  py={1}
                  align="center"
                  justify="space-between"
                  gap={2}
                >
                  <Flex align="center" gap={2}>
                    <Badge colorScheme={statusColor(s.status_code, retailer)} fontSize="2xs">
                      {s.status_code}
                    </Badge>
                    <Text fontSize="xs" color={neumorph.textPrimary}>
                      {statusLabel(s.status_code, retailer)}
                    </Text>
                  </Flex>
                  <Text fontSize="xs" fontWeight="600" color={neumorph.textPrimary}>
                    {formatNum(s.article_count)} art · {formatCurrency(s.total_soh_value)}
                  </Text>
                </Flex>
              ))}
            </Box>
          </Flex>
        )}
      </BentoCard>
      <DetailsModal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Article Status Analysis">
        <ArticleStatus supplier={supplier} retailer={retailer} />
      </DetailsModal>
    </>
  )
}

export default ArticleStatusCard
